import { css } from '@emotion/core';
import { Skeleton } from '@material-ui/lab';
import React, { useEffect, useMemo, useState } from 'react';

import { downloadFile } from '../api';

interface RenderRawHTMLSafelyProps {
    // Provide either the HTML string or a data key that will be fetched
    htmlString?: string;
    htmlDataKey?: string;
    height?: number | string; // fixed number (px) or CSS string like 'calc(100vh - 80px)'
    allowedDomains?: string[]; // e.g., ['https://*.licdn.com']
    preprocessHtml?: (html: string) => string; // allows callers to transform HTML before rendering
}

const styles = css`
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
`;

const DEFAULT_HEIGHT = 'calc(100vh - 120px)';
const skeletonRowHeight = 20;
const loadingPadding = '8px';

export const RenderRawHTMLSafely: React.FC<RenderRawHTMLSafelyProps> = ({
    htmlString,
    htmlDataKey,
    height,
    allowedDomains = [],
    preprocessHtml
}) => {
    const [fetchedHtml, setFetchedHtml] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Initialize loading to true if we're going to fetch on mount
    const [loading, setLoading] = useState<boolean>(!!htmlDataKey && !htmlString);

    // Fetch when a data key is provided (and no direct string is given or we still want to fetch)
    useEffect(() => {
        let cancelled = false;
        const shouldFetch = !!htmlDataKey && !htmlString;
        const fetchHtml = async () => {
            if (!shouldFetch) {
                setFetchedHtml(null);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const response = await downloadFile(htmlDataKey!);
                const text = await response.text();
                if (!cancelled) setFetchedHtml(text);
            } catch (e) {
                if (!cancelled) setError('Failed to load HTML source');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchHtml();
        return () => {
            cancelled = true;
        };
    }, [htmlDataKey, htmlString]);

    const html = htmlString ?? fetchedHtml ?? '';

    const iframeHeight = height ?? DEFAULT_HEIGHT;
    const srcDoc = useMemo(() => {
        const cleanedHtml = preprocessHtml ? preprocessHtml(html) : html;
        const domains = allowedDomains.join(' ');
        const csp = `
<meta http-equiv="Content-Security-Policy" content="
  default-src 'none';
  script-src 'none';
  style-src 'unsafe-inline' ${domains} data:;
  img-src ${domains} data:;
  font-src ${domains} data:;
  connect-src 'none';
  media-src 'none';
  object-src 'none';
  frame-src 'none';
  form-action 'none';
  base-uri 'none';
">`;
        return `<!doctype html><html><head><meta charset=\"utf-8\">${csp}</head><body>${cleanedHtml}</body></html>`;
    }, [html, allowedDomains, preprocessHtml]);

    let content: JSX.Element;

    if (!htmlString && !htmlDataKey) {
        content = <div className="error">No HTML source available</div>;
    } else if (loading || !html) {
        // Show skeleton while loading OR if we don't have HTML yet
        content = (
            <div className="loading" style={{ padding: loadingPadding }}>
                <Skeleton variant="rect" height={skeletonRowHeight} style={{ marginBottom: loadingPadding }} />
                <Skeleton variant="rect" height={skeletonRowHeight} style={{ marginBottom: loadingPadding }} />
                <Skeleton variant="rect" height={skeletonRowHeight} style={{ marginBottom: loadingPadding }} />
                <Skeleton variant="rect" height={skeletonRowHeight} />
            </div>
        );
    } else if (error) {
        content = <div className="error">{error ?? 'Failed to load HTML source'}</div>;
    } else {
        content = (
            <iframe
                // No tokens => maximum sandbox (no scripts, no forms, no popups, no top-nav, etc.)
                sandbox=""
                srcDoc={srcDoc}
                style={{ width: '100%', height: iframeHeight as any, border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer"
                aria-label="Untrusted content"
            />
        );
    }

    return <div css={styles}>{content}</div>;
};
